import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@/styles/tailwind-utils';
import { BusinessSettingsLayout } from '@components/layout/BusinessSettingsLayout';
import { Button } from '@components/ui/Button';

// ─── Types & constants ────────────────────────────────────────────────────────

type TimeIncrement = 15 | 20 | 30 | 60;

const INCREMENTS: TimeIncrement[] = [15, 20, 30, 60];
const DEFAULT_INCREMENT: TimeIncrement = 15;

// Calendar rendering constants
const PX_PER_MIN = 1.5;  // 90px per hour
const START_HOUR = 9;    // 9:00 AM
const END_HOUR = 12;     // 12:00 PM
const FADE_HOUR = 11;    // fade-out begins at 11:00 AM

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtHourLabel(minutesFromMidnight: number): string {
  const h = Math.floor(minutesFromMidnight / 60);
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${period}`;
}

// ─── Calendar preview ─────────────────────────────────────────────────────────

const TIME_COL_W = 64; // px

function CalendarDayHeader() {
  const today = new Date();
  const dayName = today.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const dateNum = today.getDate();

  return (
    <div className="flex items-center border-b border-border px-0 py-2">
      <div className="shrink-0" style={{ width: TIME_COL_W }} />
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-muted-foreground">{dayName}</span>
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand text-[11px] font-bold text-white">
          {dateNum}
        </span>
      </div>
    </div>
  );
}

function CalendarPreview({ increment }: { increment: TimeIncrement }) {
  const totalMinutes = (END_HOUR - START_HOUR) * 60;
  const totalHeight = totalMinutes * PX_PER_MIN;
  const fadeTop = (FADE_HOUR - START_HOUR) * 60 * PX_PER_MIN;

  // Hour boundary positions (including the closing END_HOUR line)
  const hourOffsets = Array.from(
    { length: END_HOUR - START_HOUR + 1 },
    (_, i) => i * 60,
  );

  // Sub-hour increment line positions
  const subHourOffsets: number[] = [];
  for (let m = 0; m <= totalMinutes; m += increment) {
    if (m % 60 !== 0) subHourOffsets.push(m);
  }

  const appointments = [
    { start: 0,   end: increment, label: 'Consultation',  cls: 'bg-brand/10 border-brand text-brand' },
    { start: 120, end: 180,       label: 'Physiotherapy', cls: 'bg-sky-50 border-sky-400 text-sky-700' },
  ];

  return (
    <div className="overflow-hidden">
      <CalendarDayHeader />
      <div className="relative select-none" style={{ height: totalHeight }}>
        {/* Hour grid lines + time labels */}
        {hourOffsets.map((offset) => (
          <div
            key={offset}
            className="absolute left-0 right-0 flex items-start"
            style={{ top: offset * PX_PER_MIN }}
          >
            <span
              className="-mt-2 shrink-0 pr-2 text-right text-[10px] leading-none text-muted-foreground"
              style={{ width: TIME_COL_W - 4 }}
            >
              {offset < totalMinutes ? fmtHourLabel((START_HOUR + offset / 60) * 60) : ''}
            </span>
            <div
              className="flex-1 border-t border-brand/30"
              style={{ boxShadow: '0 0 6px 2px hsl(var(--color-brand) / 0.16)' }}
            />
          </div>
        ))}

        {/* Sub-hour increment lines — same flex layout as hour lines for reliable rendering */}
        {subHourOffsets.map((offset) => (
          <div
            key={offset}
            className="absolute left-0 right-0 flex items-start"
            style={{ top: offset * PX_PER_MIN }}
          >
            <div className="shrink-0" style={{ width: TIME_COL_W }} />
            <div
              className="flex-1 border-t border-dashed border-brand/20"
              style={{ boxShadow: '0 0 4px 1px hsl(var(--color-brand) / 0.09)' }}
            />
          </div>
        ))}

        {/* Sample appointment blocks */}
        {appointments.map((apt, i) => {
          const top = apt.start * PX_PER_MIN;
          const height = (apt.end - apt.start) * PX_PER_MIN;
          if (height < 14) return null;
          return (
            <div
              key={i}
              className={cn(
                'absolute overflow-hidden rounded-sm border-l-2 px-2 py-0.5',
                apt.cls,
              )}
              style={{ top, height, left: TIME_COL_W + 4, right: 8 }}
            >
              <span className="block truncate text-xs font-medium leading-tight">
                {apt.label}
              </span>
              {height >= 28 && (
                <span className="block text-[10px] opacity-70">
                  {fmtHourLabel((START_HOUR + apt.start / 60) * 60)
                    .replace(':00', `:${String((apt.start % 60) || '00').padStart(2, '0')}`)}
                </span>
              )}
            </div>
          );
        })}

        {/* Fade overlay: content from FADE_HOUR to END_HOUR fades out */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-b from-transparent to-surface"
          style={{ top: fadeTop }}
        />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ScheduleSettingsPage() {
  const { t } = useTranslation();

  const [saved, setSaved] = useState<TimeIncrement>(DEFAULT_INCREMENT);
  const [selected, setSelected] = useState<TimeIncrement>(DEFAULT_INCREMENT);
  const [justSaved, setJustSaved] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  function handleSave() {
    setSaved(selected);
    setJustSaved(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setJustSaved(false), 2500);
  }

  function handleDiscard() {
    setSelected(saved);
  }

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <BusinessSettingsLayout>
      <div className="space-y-6">
        {/* ── Settings card ──────────────────────────────────────────────── */}
        <div className="rounded-md border border-border bg-surface">
          {/* Header */}
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-base font-semibold text-foreground">
              {t('settings.scheduleSettings.title')}
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {t('settings.scheduleSettings.subtitle')}
            </p>
          </div>

          {/* Time increment section */}
          <div className="px-6 py-6">
            <h3 className="text-sm font-semibold text-foreground">
              {t('settings.scheduleSettings.timeIncrement.label')}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('settings.scheduleSettings.timeIncrement.description')}
            </p>

            {/* Option cards */}
            <div className="mt-4 grid grid-cols-4 gap-3">
              {INCREMENTS.map((inc) => (
                <button
                  key={inc}
                  type="button"
                  onClick={() => setSelected(inc)}
                  className={cn(
                    'flex flex-col items-center justify-center rounded-md border py-6 transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                    selected === inc
                      ? 'border-brand bg-brand-subtle'
                      : 'border-border bg-surface hover:border-brand/40 hover:bg-brand-subtle/30',
                  )}
                >
                  <span
                    className={cn(
                      'text-3xl font-semibold leading-none',
                      selected === inc ? 'text-brand' : 'text-foreground',
                    )}
                  >
                    {inc}
                  </span>
                  <span className="mt-1.5 text-xs text-muted-foreground">
                    {t('settings.scheduleSettings.timeIncrement.minutes')}
                  </span>
                </button>
              ))}
            </div>

            {/* Current setting */}
            <p className="mt-3 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {t('settings.scheduleSettings.timeIncrement.currentSetting', {
                  value: saved,
                })}
              </span>
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-border px-6 py-4">
            <div>
              {selected !== saved ? (
                <p className="text-sm text-muted-foreground">
                  {t('settings.unsavedChanges')}
                </p>
              ) : justSaved ? (
                <p className="text-sm font-medium text-brand">
                  {t('settings.scheduleSettings.saved')}
                </p>
              ) : null}
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="ghost" onClick={handleDiscard}>
                {t('settings.scheduleSettings.discardChanges')}
              </Button>
              <Button type="button" onClick={handleSave} disabled={selected === saved}>
                {t('settings.scheduleSettings.saveChanges')}
              </Button>
            </div>
          </div>
        </div>

        {/* ── Day view preview card ──────────────────────────────────────── */}
        <div className="rounded-md border border-border bg-surface">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-base font-semibold text-foreground">
              {t('settings.scheduleSettings.preview.title')}
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {t('settings.scheduleSettings.preview.subtitle')}
            </p>
          </div>
          <div className="px-6 py-4">
            <CalendarPreview increment={selected} />
          </div>
        </div>
      </div>
    </BusinessSettingsLayout>
  );
}
